<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in items" :key="index">
        <template #header>
          <VaSidebarItem
            :to="{ name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`Visit ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const items = [
      {
        id: 1,
        displayName: 'menu.dashboard',
        meta: { icon: 'dashboard' },
        name: 'dashboard',
      },
      {
        id: 2,
        displayName: 'menu.orders',
        meta: { icon: 'reorder' },
        name: 'orders',
      },
      {
        id: 3,
        displayName: 'menu.payments',
        meta: { icon: 'payment' },
        name: 'payments',
      },
      {
        id: 4,
        displayName: 'menu.contacts',
        meta: { icon: '3p' },
        name: 'contacts',
      },
      {
        id: 5,
        displayName: 'menu.emails',
        meta: { icon: 'email' },
        name: 'emails',
      },
      {
        id: 6,
        displayName: 'menu.categories',
        meta: { icon: 'category' },
        name: 'categories',
      },
      {
        id: 7,
        displayName: 'menu.products',
        meta: { icon: 'inventory_2' },
        name: 'products',
      },
      {
        id: 8,
        displayName: 'menu.payment-methods',
        meta: { icon: 'account_balance_wallet' },
        name: 'payment-methods',
      },
      {
        id: 9,
        displayName: 'menu.prepayment-configs',
        meta: { icon: 'settings' },
        name: 'prepayment-configs',
      },
    ]

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    // const setActiveExpand = () =>
    //   (value.value = items.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    // watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      items,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
